import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LANGUAGE_COOKIE = 'admin_language';

const localeFiles = require.context('./locales', false, /\.json$/);
const resources = localeFiles.keys().reduce((result, fileName) => {
  // NODE_PATH=./src makes webpack expose each file through both its relative
  // request (`./de.json`) and its source-root request
  // (`i18n/locales/de.json`). Reduce both requests to the same locale key.
  const language = fileName
    .split('/')
    .pop()
    .replace(/\.json$/i, '')
    .toLowerCase();
  const locale = localeFiles(fileName);
  result[language] = { translation: locale.default || locale };
  return result;
}, {});

const preferredLanguageOrder = ['en', 'de', 'es', 'it', 'pt', 'ru', 'ja', 'cn', 'pl', 'cs', 'el', 'da'];
const discoveredLanguages = [...new Set(Object.keys(resources).map((language) => language.toLowerCase()))];

export const SUPPORTED_LANGUAGES = Object.freeze([
  ...preferredLanguageOrder.filter((language) => discoveredLanguages.includes(language)),
  ...discoveredLanguages
    .filter((language) => !preferredLanguageOrder.includes(language))
    .sort((first, second) => first.localeCompare(second)),
]);

const normalizeLanguage = (language) => {
  const normalized = String(language || '')
    .trim()
    .slice(0, 2)
    .toLowerCase();

  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : null;
};

export const getLanguageCookie = () => {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(`${LANGUAGE_COOKIE}=`));

  return normalizeLanguage(cookie ? decodeURIComponent(cookie.split('=')[1]) : null);
};

export const persistLanguage = (language) => {
  if (typeof document === 'undefined') return;

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${LANGUAGE_COOKIE}=${encodeURIComponent(language)}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
};

export const changeLanguage = (language, persist = true) => {
  const normalized = normalizeLanguage(language) || 'en';

  // i18next emits `languageChanged` synchronously, which remounts the
  // localized application tree. Persist first so the authentication startup
  // sync cannot overwrite an explicit selection during that remount.
  if (persist) persistLanguage(normalized);
  if (typeof document !== 'undefined') document.documentElement.lang = normalized;

  return i18n.changeLanguage(normalized);
};

export const syncLanguageFromUser = (userLanguage) => {
  if (getLanguageCookie()) return;

  const normalized = normalizeLanguage(userLanguage);
  if (normalized) changeLanguage(normalized, true);
};

const initialLanguage = getLanguageCookie() || 'en';

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: 'en',
  // i18next transforms this option internally, so do not give it the array
  // that is also used to render the language selector.
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: { escapeValue: false },
  returnNull: false,
});

if (typeof document !== 'undefined') document.documentElement.lang = initialLanguage;

export const t = (...args) => i18n.t(...args);

export default i18n;
